import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import Input from "../../componentes/input/input";
import { Loading } from "../../componentes/loading/loading";
import TipoServicoTIService from "../../services/resource/tipoServicoTIService";

const service = new TipoServicoTIService();

const TIPO_CAMPO_OPTIONS = [
    { value: "TEXTO", label: "Texto curto" },
    { value: "TEXTO_LONGO", label: "Texto longo" },
    { value: "NUMERO", label: "Número" },
    { value: "DATA", label: "Data" },
    { value: "BOOLEANO", label: "Sim/Não" },
    { value: "SELECAO", label: "Seleção (opções)" },
    { value: "LOCALIZACAO", label: "Localização (GPS)" },
    { value: "IMAGEM", label: "Imagem (anexo)" },
];

function slugify(texto) {
    return (texto || "")
        .normalize("NFD").replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
}

const CAMPO_INICIAL = () => ({ id: null, chave: "", rotulo: "", tipoCampo: "TEXTO", obrigatorio: false, opcoes: [], chaveEditadaManualmente: false });
const TIPO_INICIAL = { id: null, nome: "", ativo: true, campos: [] };

/** Admin: cadastro dos Tipos de Serviço de TI usados na Etapa 1 de Abertura de Chamado TI — cada
 * tipo define seu próprio conjunto de campos extras, exibidos dinamicamente quando selecionado. */
export default function AdminTiposServicoTI() {
    const [tipos, setTipos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(TIPO_INICIAL);
    const [erro, setErro] = useState("");
    const [paraExcluir, setParaExcluir] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    useEffect(() => {
        document.title = "MyDash | Tipos de Serviço de TI";
        carregar();
    }, []);

    function carregar() {
        setLoading(true);
        service.listar()
            .then(res => setTipos(res.data))
            .catch(() => setTipos([]))
            .finally(() => setLoading(false));
    }

    function abrirModal(tipo = null) {
        setErro("");
        if (tipo) {
            setForm({
                id: tipo.id,
                nome: tipo.nome,
                ativo: tipo.ativo,
                campos: (tipo.campos || []).map(c => ({ ...c, chaveEditadaManualmente: true, opcoes: c.opcoes || [] })),
            });
        } else {
            setForm(TIPO_INICIAL);
        }
        setModalOpen(true);
    }

    function fecharModal() {
        setModalOpen(false);
    }

    function adicionarCampo() {
        setForm(prev => ({ ...prev, campos: [...prev.campos, CAMPO_INICIAL()] }));
    }

    function removerCampo(index) {
        setForm(prev => ({ ...prev, campos: prev.campos.filter((_, i) => i !== index) }));
    }

    function atualizarCampo(index, alteracoes) {
        setForm(prev => ({
            ...prev,
            campos: prev.campos.map((c, i) => {
                if (i !== index) return c;
                const campo = { ...c, ...alteracoes };
                if (alteracoes.rotulo !== undefined && !campo.chaveEditadaManualmente) {
                    campo.chave = slugify(alteracoes.rotulo);
                }
                if (alteracoes.chave !== undefined) {
                    campo.chaveEditadaManualmente = true;
                }
                return campo;
            }),
        }));
    }

    function salvarForm(e) {
        e.preventDefault();
        setErro("");

        if (!form.nome.trim()) {
            setErro("Informe o nome do tipo de serviço");
            return;
        }
        for (const campo of form.campos) {
            if (!campo.rotulo.trim() || !campo.chave.trim()) {
                setErro("Todo campo precisa de um rótulo");
                return;
            }
        }
        const chaves = form.campos.map(c => c.chave);
        if (new Set(chaves).size !== chaves.length) {
            setErro("Há campos com a mesma chave — ajuste os rótulos para diferenciá-los");
            return;
        }

        const dto = {
            nome: form.nome,
            ativo: form.ativo,
            campos: form.campos.map((c, index) => ({
                id: c.id,
                chave: c.chave,
                rotulo: c.rotulo,
                tipoCampo: c.tipoCampo,
                obrigatorio: c.obrigatorio,
                opcoes: c.tipoCampo === "SELECAO" ? c.opcoes.filter(o => o.trim()) : [],
                ordem: index,
            })),
        };

        setSaving(true);
        const requisicao = form.id ? service.atualizar(form.id, dto) : service.salvar(dto);
        requisicao
            .then(() => { fecharModal(); carregar(); })
            .catch(err => setErro(err?.response?.data?.message || "Não foi possível salvar o tipo de serviço"))
            .finally(() => setSaving(false));
    }

    function confirmarExclusao(tipo) {
        setParaExcluir(tipo);
        setDeleteModalOpen(true);
    }

    function excluirConfirmado() {
        service.excluir(paraExcluir.id)
            .then(() => { setDeleteModalOpen(false); carregar(); })
            .catch(() => setDeleteModalOpen(false));
    }

    return (
        <div className="min-h-screen bg-background p-4 md:p-8">
            <div className="mx-auto">
                <h1 className="font-display text-2xl font-bold text-foreground mb-1">Tipos de Serviço de TI</h1>
                <p className="text-sm text-muted-foreground mb-4">
                    Cadastre os tipos de serviço disponíveis na abertura de chamado — cada tipo pode definir campos extras próprios, exibidos automaticamente quando ele é selecionado.
                </p>

                <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
                    <div className="flex flex-col gap-4 bg-primary px-4 py-3 sm:flex-row sm:items-center sm:justify-between md:px-7">
                        <h2 className="font-display text-lg font-bold text-primary-foreground">Tipos de Serviço</h2>
                        <button
                            onClick={() => abrirModal()}
                            className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-sm font-semibold text-primary transition-colors duration-150 hover:bg-muted"
                        >
                            <i className="fas fa-plus mr-2" /> Novo Tipo de Serviço
                        </button>
                    </div>

                    {loading ? (
                        <Loading />
                    ) : (
                        <div className="overflow-x-auto fade-in">
                            <table className="min-w-full divide-y divide-border">
                                <thead className="bg-table-header">
                                    <tr className="text-sm">
                                        <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Nome</th>
                                        <th className="py-3 px-6 text-center text-xs font-medium text-table-header-fg uppercase tracking-wider">Ativo</th>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Campos</th>
                                        <th className="py-3 px-6 text-right text-xs font-medium text-table-header-fg uppercase tracking-wider">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border bg-card">
                                    {tipos.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-4 text-center text-muted-foreground">Nenhum tipo de serviço cadastrado</td>
                                        </tr>
                                    ) : (
                                        tipos.map(t => (
                                            <tr key={t.id} className="text-xs transition-colors duration-150 hover:bg-table-row-hover">
                                                <td className="px-6 py-3 whitespace-nowrap font-medium text-foreground">{t.nome}</td>
                                                <td className="px-6 py-3 whitespace-nowrap text-center">
                                                    <span className={`inline-flex rounded-md px-2 py-1 text-[10px] font-semibold ${t.ativo ? "bg-green/10 text-green" : "bg-muted text-muted-foreground"}`}>
                                                        {t.ativo ? "Ativo" : "Inativo"}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-3 text-muted-foreground">
                                                    {(t.campos || []).length === 0 ? "-" : t.campos.map(c => c.rotulo).join(", ")}
                                                </td>
                                                <td className="px-3 py-3 whitespace-nowrap text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <i onClick={() => abrirModal(t)} className="fa fa-pencil-square text-primary hover:text-primary/80 p-1 rounded-full hover:bg-primary/10 cursor-pointer" />
                                                        <i onClick={() => confirmarExclusao(t)} className="fa fa-times text-destructive hover:text-destructive/80 p-1 rounded-full hover:bg-destructive/10 cursor-pointer" />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <Dialog
                header={form.id ? "Atualizar Tipo de Serviço" : "Novo Tipo de Serviço"}
                onHide={fecharModal}
                visible={modalOpen}
                blockScroll
                className="w-[95%] max-w-3xl"
                footer={
                    <div className="flex justify-end space-x-3 mt-3">
                        <button type="button" onClick={fecharModal} className="px-4 py-2 bg-card border border-input rounded-md transition-colors duration-150 hover:bg-muted">Cancelar</button>
                        <button
                            type="submit"
                            onClick={salvarForm}
                            disabled={saving}
                            className="px-4 py-2 bg-primary hover:brightness-95 text-primary-foreground rounded-md transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving && <i className="fas fa-spinner fa-spin" />}
                            {saving ? "Salvando..." : "Salvar"}
                        </button>
                    </div>
                }
            >
                <form className="p-4 flex flex-col gap-5" onSubmit={salvarForm}>
                    {erro && (
                        <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-sm text-destructive">{erro}</div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-end">
                        <Input
                            label="Nome do tipo de serviço"
                            required
                            placeholder="Ex: Control"
                            value={form.nome}
                            onChange={e => setForm(prev => ({ ...prev, nome: e.target.value }))}
                        />
                        <div className="flex items-center gap-2 pb-2">
                            <InputSwitch checked={!!form.ativo} onChange={e => setForm(prev => ({ ...prev, ativo: e.value }))} />
                            <span className="text-sm text-foreground/80">Ativo</span>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-foreground/80">Campos específicos deste tipo</label>
                            <button type="button" onClick={adicionarCampo} className="text-xs font-semibold text-primary hover:underline">
                                <i className="fas fa-plus mr-1" /> Adicionar campo
                            </button>
                        </div>

                        {form.campos.length === 0 ? (
                            <p className="text-xs text-muted-foreground">Nenhum campo extra — só resumo, descrição e tipo de serviço serão pedidos.</p>
                        ) : (
                            <div className="flex flex-col gap-3">
                                {form.campos.map((campo, index) => (
                                    <CampoDinamicoRow
                                        key={index}
                                        campo={campo}
                                        onChange={alteracoes => atualizarCampo(index, alteracoes)}
                                        onRemover={() => removerCampo(index)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </form>
            </Dialog>

            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card rounded-lg shadow-pop w-full max-w-md p-6">
                        <p className="text-foreground">Você confirma a exclusão do tipo de serviço "{paraExcluir?.nome}"?</p>
                        <div className="flex justify-end space-x-3 mt-4">
                            <button onClick={() => setDeleteModalOpen(false)} className="px-4 py-2 border border-input rounded-md transition-colors duration-150 hover:bg-muted">Cancelar</button>
                            <button onClick={excluirConfirmado} className="px-4 py-2 rounded-md transition-all duration-150 bg-destructive text-destructive-foreground hover:brightness-95">Excluir</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CampoDinamicoRow({ campo, onChange, onRemover }) {
    return (
        <div className="rounded-lg border border-border p-3 flex flex-col gap-3 bg-muted/30">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                <Input
                    label="Rótulo"
                    required
                    placeholder="Ex: Número do Control"
                    value={campo.rotulo}
                    onChange={e => onChange({ rotulo: e.target.value })}
                />
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Tipo de campo</label>
                    <CustomDropdown value={campo.tipoCampo} onChange={e => onChange({ tipoCampo: e.value })} options={TIPO_CAMPO_OPTIONS} />
                </div>
                <button type="button" onClick={onRemover} className="h-9 px-3 text-destructive hover:bg-destructive/10 rounded-md transition-colors duration-150" title="Remover campo">
                    <i className="fa fa-trash" />
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-foreground/80">
                    <InputSwitch checked={!!campo.obrigatorio} onChange={e => onChange({ obrigatorio: e.value })} />
                    Obrigatório
                </label>
                <span className="text-xs text-muted-foreground">Chave: <code>{campo.chave || "-"}</code></span>
            </div>

            {campo.tipoCampo === "SELECAO" && (
                <Input
                    label="Opções"
                    placeholder="Separe as opções por vírgula"
                    value={(campo.opcoes || []).join(", ")}
                    onChange={e => onChange({ opcoes: e.target.value.split(",").map(o => o.trim()) })}
                    helpText="Ex: Aberto, Em andamento, Concluído"
                />
            )}
        </div>
    );
}
